import React, {useCallback, useContext, useRef, useState} from 'react'
import {defineMessages} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {UpdateLocationOptionsType} from 'components/data/WithUrlUpdater'
import {DestinationPickerOverlay} from 'components/DestinationPickerOverlay'
import {addDays} from 'date-fns/addDays'
import {format} from 'date-fns/format'
import {
  RecentSearchParams,
  useSearchBox,
  useSearchBoxDatePicker,
  useSearchBoxDestinationPicker,
  useSearchBoxGuestPicker
} from 'hooks/searchBox'
import {useDestinationDisplayName} from 'hooks/useDestinationDisplayName'
import {usePageLoadEndTimer} from 'hooks/usePageLoad'
import useShowOverlay, {
  useShowDatePickerOverlay
} from 'hooks/useShowOverlay/useShowOverlay'
import {getDevicePlatform, getMeta} from 'modules/meta/selectors'
import {
  getIsDatePickerOpen,
  getIsDestinationPickerOpen,
  getIsGuestPickerOpen
} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {OverlayType} from 'modules/overlay/types'
import {
  getSearchParameters,
  getShowTotalPrices
} from 'modules/sapiSearch/selectors'
import {getIsMultiRoomBundlesSearch} from 'modules/search/selectors'
import {
  getOpenDatePickerType,
  getSearchBoxValues
} from 'modules/searchBox/selectors'
import {closeSearchBox} from 'modules/searchBox/slice'
import {SearchBoxValues, SearchTrigger} from 'modules/searchBox/types'
import {getUserSearchHistory} from 'utils/persistence'

import {TRANSITION_DURATION} from '@vio/atlas/src/components/designSystem/Overlay'
import {cssTheme} from '@vio/atlas/themes'
import {Entity} from '@vio/core/src/analytics/types/Events'
import {DatePickerType} from '@vio/core/src/datePicker/types'
import {ConfigureI18nContext} from '@vio/core/src/localization/components/ConfigureI18n'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {
  numberOfGuests,
  roomsSplitToString,
  roomsToConfigurationObject
} from '@vio/core/src/room/business/roomConfiguration'
import {
  RoomConfigType,
  RoomSplitType
} from '@vio/core/src/room/types/RoomConfiguration'
import {WithColorPriceDayData} from '@vio/core/src/search/modules/ColorPriceCalendar/components/WithColorPriceDayData'
import {useColorPriceCalendar} from '@vio/core/src/search/modules/ColorPriceCalendar/hooks'
import {dateToMiddayDate, UTS_DATE_FORMAT} from '@vio/core/src/utils/date'
import {DatePickerOverlay} from '@vio/shared/src/search/DatePickerOverlay'
import {GuestRoomConfigOverlay} from '@vio/shared/src/search/GuestRoomConfig/GuestRoomConfigOverlay'
import {SearchFormVertical} from '@vio/shared/src/search/SearchFormVertical'

const SearchBoxWrapperBase = styled('div')`
  background: ${cssTheme.colors.background.neutral.primary};
`

const destinationErrorMessages = defineMessages({
  unknown: {
    id: 'destinationPickerUnknownMessage',
    defaultMessage: "Sorry, we don't recognize that location"
  },
  empty: {
    id: 'destinationPickerEmptyMessage',
    defaultMessage: 'Tell us where you would like to go'
  }
})

type Props = {
  searchOptions?: UpdateLocationOptionsType
  additionalSearchParams?: Partial<SearchBoxValues>
}

export const SearchFormMobile = ({
  searchOptions,
  additionalSearchParams = {homeSearch: '1'}
}: Props) => {
  const dispatch = useDispatch()
  const isAndroid = useSelector(getDevicePlatform) === 'android'
  const destinationInputRef = useRef<HTMLInputElement>(null)
  const showOverlay = useShowOverlay()
  const showDatePickerOverlay = useShowDatePickerOverlay()

  const [scrollY, setScrollY] = useState(null)
  const [isGeolocationSearchSelected, setIsGeolocationSearchSelected] =
    useState(false)

  // Use the value from the searchbox as the value in Search won't
  // get set until the search has been made and the URL param has been added
  const {optimizeRooms} = useSelector(getSearchBoxValues)
  const isOptimizeRooms = Boolean(optimizeRooms)

  const userSearchHistory = getUserSearchHistory()
  const latestUserSearch = userSearchHistory?.[0]
  const today = dateToMiddayDate(new Date())
  const tomorrow = addDays(today, 1)
  const todayString = format(today, UTS_DATE_FORMAT)
  const tomorrowString = format(tomorrow, UTS_DATE_FORMAT)
  const initialDatePickerValues = {
    checkIn: latestUserSearch?.searchParams.checkIn || todayString,
    checkOut: latestUserSearch?.searchParams.checkOut || tomorrowString
  }
  const initialGuestPickerValues = {
    rooms: latestUserSearch?.searchParams.rooms,
    optimizeRooms: latestUserSearch
      ? latestUserSearch.searchParams.optimizeRooms
      : true
  }

  const {doSearch, onSearchButtonClick} = useSearchBox()
  const {
    destinationHasError,
    onDestinationChange,
    onDestinationPickerInputClick,
    onRecentSearchSelected,
    setDestinationHasError,
    destinationDisplayValue,
    destinationPickerInputDisplayValue,
    onSuggestionsRequested,
    onNearbyPropertiesSuggestionSelected
  } = useSearchBoxDestinationPicker()
  const {
    onDateChange,
    onDatePickerClose,
    onDatePickerDayClick,
    onDatePickerOpen,
    onDatePickerInputClick,
    onDatePickerSubmit,
    onDatePickerReset
  } = useSearchBoxDatePicker(initialDatePickerValues)
  const {
    onGuestRoomsChange,
    onGuestOptimizeRoomsChange,
    onGuestPickerClick,
    onGuestPickerSubmit,
    onGuestPickerReset,
    onGuestPickerDisplayed,
    onGuestPickerOptionInfoDisplayed,
    onGuestConfigPreferenceClick
  } = useSearchBoxGuestPicker(initialGuestPickerValues)

  const {checkIn, checkOut, hotelId, placeId, address, rooms, iataCode} =
    useSelector(getSearchBoxValues)
  const searchBoxDestinationDisplayValue = useDestinationDisplayName()
  const showTotalPrices = useSelector(getShowTotalPrices)
  const searchParameters = useSelector(getSearchParameters)
  const combinedSearchParameters = {
    ...searchParameters,
    rooms,
    checkIn,
    checkOut
  }
  const {includeLocalTaxes, includeTaxes} = useSelector(getMeta)
  const isMultiRoomBundlesSearch = useSelector(getIsMultiRoomBundlesSearch)
  const pricingContext = {
    includeTaxes,
    includeLocalTaxes,
    includeRoomsInNightlyPrice: isMultiRoomBundlesSearch
  }
  const numberOfGuestsValue = numberOfGuests(rooms)

  const isColorPriceDatePickerEnabled =
    numberOfGuestsValue <= 2 && Boolean(hotelId)

  const {monthNames, weekDayNamesShort, firstDayOfWeek} =
    useContext(ConfigureI18nContext)

  const {numberOfAdults, numberOfChildren, numberOfRooms, roomsSplit} =
    roomsToConfigurationObject(rooms)

  const openedDatePickerType = useSelector(getOpenDatePickerType)

  const isDestinationPickerOpen = useSelector(getIsDestinationPickerOpen)

  const isDatePickerOpen = useSelector(getIsDatePickerOpen)

  const isGuestPickerOpen = useSelector(getIsGuestPickerOpen)

  const {requestOneMonthAvailability} = useColorPriceCalendar({
    searchParameters: combinedSearchParameters,
    pricingContext
  })

  const restoreScrollPosition = useCallback(() => {
    if (!scrollY) return

    window.scrollTo(0, scrollY)
    setScrollY(null)
  }, [scrollY])

  const handleCloseOverlays = useCallback(() => {
    restoreScrollPosition()
    dispatch(hideOverlay())
  }, [restoreScrollPosition, dispatch])

  const handleSuggestionSelected = useCallback(() => {
    restoreScrollPosition()
    setIsGeolocationSearchSelected(false)
    handleCloseOverlays()

    setTimeout(() => {
      showDatePickerOverlay(DatePickerType.CheckIn)
    }, TRANSITION_DURATION)
  }, [
    showDatePickerOverlay,
    handleCloseOverlays,
    restoreScrollPosition,
    setIsGeolocationSearchSelected
  ])

  const handleRecentSearchSelected = useCallback(
    (recentSearch: RecentSearchParams): void => {
      setIsGeolocationSearchSelected(false)
      onRecentSearchSelected({
        recentSearch,
        additionalSearchParams
      })
    },
    [onRecentSearchSelected, setIsGeolocationSearchSelected]
  )

  const handleDestinationPickerClick = useCallback((): void => {
    showOverlay({
      type: OverlayType.DestinationPicker
    })
    setDestinationHasError(false)
    setScrollY(window.scrollY)

    onDestinationPickerInputClick()
    destinationInputRef?.current?.focus?.()
    window.scrollTo(0, 0)
  }, [setDestinationHasError, onDestinationPickerInputClick, showOverlay])

  const openDatePickerControl = useCallback(
    (type: DatePickerType): void => {
      showDatePickerOverlay(type)
      onDatePickerInputClick(type)
      onDatePickerOpen(type)
    },
    [onDatePickerOpen, onDatePickerInputClick, showDatePickerOverlay]
  )

  const handleDatePickerClose = useCallback((): void => {
    onDatePickerClose()
    onDatePickerReset()
    handleCloseOverlays()
  }, [onDatePickerReset, onDatePickerClose, handleCloseOverlays])

  const handleDatePickerSubmit = useCallback((): void => {
    onDatePickerSubmit()
    handleCloseOverlays()
  }, [onDatePickerSubmit, handleCloseOverlays])

  const handleGuestPickerClick = useCallback((): void => {
    showOverlay({
      type: OverlayType.GuestPicker,
      entity: Entity.GuestPicker
    })
    onGuestPickerClick()
    onGuestPickerDisplayed()
  }, [onGuestPickerClick, onGuestPickerDisplayed, showOverlay])

  const handleCloseGuestPicker = useCallback(() => {
    onGuestPickerReset()
    handleCloseOverlays()
  }, [onGuestPickerReset, handleCloseOverlays])

  const handleGuestRoomsChange = useCallback(
    (roomsSplit: RoomSplitType[]): void => {
      const rooms = roomsSplitToString(roomsSplit)
      onGuestRoomsChange(rooms)
    },
    [onGuestRoomsChange]
  )

  const handleGuestOptimizeRoomsChange = useCallback(
    (isOptimizeRooms?: boolean): void => {
      onGuestOptimizeRoomsChange(isOptimizeRooms)
    },
    [onGuestOptimizeRoomsChange]
  )

  const handleGuestPickerSubmit = useCallback(
    (rooms: RoomSplitType[]): void => {
      onGuestPickerSubmit()
      handleCloseOverlays()
      handleGuestRoomsChange(rooms)
    },
    [handleGuestRoomsChange, onGuestPickerSubmit, handleCloseOverlays]
  )

  const handleSubmit = useCallback(
    (event?: React.SyntheticEvent | null | undefined): void | boolean => {
      event?.preventDefault()

      if (
        !placeId &&
        !hotelId &&
        !isGeolocationSearchSelected &&
        !address &&
        !iataCode
      ) {
        setDestinationHasError(true)
        return false
      }

      onSearchButtonClick()
      dispatch(closeSearchBox())

      doSearch({
        newUrlParams: additionalSearchParams,
        searchTrigger: SearchTrigger.Submit,
        searchOptions
      })
    },
    [
      placeId,
      hotelId,
      isGeolocationSearchSelected,
      address,
      onSearchButtonClick,
      doSearch,
      setDestinationHasError,
      searchBoxDestinationDisplayValue
    ]
  )

  const handleNearbyPropertiesSuggestionSelected = useCallback(() => {
    onNearbyPropertiesSuggestionSelected(false)
    setIsGeolocationSearchSelected(true)
    handleCloseOverlays()

    setTimeout(() => {
      openDatePickerControl(DatePickerType.CheckIn)
      showDatePickerOverlay(DatePickerType.CheckIn)
    }, TRANSITION_DURATION)
  }, [
    onNearbyPropertiesSuggestionSelected,
    openDatePickerControl,
    showDatePickerOverlay,
    handleCloseOverlays
  ])

  usePageLoadEndTimer()

  return (
    <SearchBoxWrapperBase>
      <SearchFormVertical
        destination={destinationDisplayValue}
        checkIn={checkIn}
        checkOut={checkOut}
        numberOfAdults={numberOfAdults}
        numberOfChildren={numberOfChildren}
        numberOfRooms={numberOfRooms}
        onDatePickerClick={openDatePickerControl}
        onDestinationPickerClick={handleDestinationPickerClick}
        onGuestPickerClick={handleGuestPickerClick}
        onSubmit={handleSubmit}
        destinationHasError={destinationHasError}
        destinationErrorMessage={
          searchBoxDestinationDisplayValue
            ? destinationErrorMessages.unknown
            : destinationErrorMessages.empty
        }
        isRoomsLabelShown={!isOptimizeRooms}
      />
      <DestinationPickerOverlay
        isOpen={isDestinationPickerOpen}
        displayValue={destinationPickerInputDisplayValue}
        onSuggestionsRequested={onSuggestionsRequested}
        onChange={onDestinationChange}
        onSuggestionSelected={handleSuggestionSelected}
        onRecentSearchSelected={handleRecentSearchSelected}
        onClose={handleCloseOverlays}
        onNearbyPropertiesSuggestionSelected={
          handleNearbyPropertiesSuggestionSelected
        }
        destinationInputRef={destinationInputRef}
      />
      <DatePickerOverlay
        virtualize={isAndroid}
        isOpen={isDatePickerOpen}
        checkIn={checkIn}
        checkOut={checkOut}
        firstDayOfWeek={firstDayOfWeek}
        months={monthNames}
        weekdaysShort={weekDayNamesShort}
        openedDatePickerType={openedDatePickerType}
        onDayClick={onDatePickerDayClick}
        onOpen={onDatePickerOpen}
        onClose={handleDatePickerClose}
        onChange={onDateChange}
        onSubmit={handleDatePickerSubmit}
        submitButtonMessage={
          <FormattedMessageWrapper id="apply" defaultMessage="Apply" />
        }
        showTotalPrices={showTotalPrices}
        shouldShowPrices
        onMonthView={date => requestOneMonthAvailability(date, [hotelId])}
        isColorPriceDatePickerEnabled={isColorPriceDatePickerEnabled}
        DayInnerContainer={WithColorPriceDayData}
      />
      <GuestRoomConfigOverlay
        isOpen={isGuestPickerOpen}
        roomsSplit={roomsSplit}
        onClose={handleCloseGuestPicker}
        onRoomsChange={handleGuestRoomsChange}
        onOptimizeRoomsChange={handleGuestOptimizeRoomsChange}
        onSubmit={handleGuestPickerSubmit}
        onInfoOpen={onGuestPickerOptionInfoDisplayed}
        onGuestConfigOptionClick={onGuestConfigPreferenceClick}
        roomConfigType={
          optimizeRooms ? RoomConfigType.Optimized : RoomConfigType.Manual
        }
        submitButtonMessage={
          <FormattedMessageWrapper id="apply" defaultMessage="Apply" />
        }
      />
    </SearchBoxWrapperBase>
  )
}
